/* Mariposa path gate and switcher.
   The path lives on the cart attribute `shopping_mode` because that is the only
   shopper-carried value Liquid can read at render time. Choosing writes the
   attribute and reloads, because the attribute is written after the current
   page rendered and therefore cannot gate the page that set it.
   See DESIGN-PATH-MODEL.md. */
(function () {
  var ATTR = 'shopping_mode';
  var MEMORY = 'mariposa_retail_mode';
  function remember(path) {
    if (path !== 'pickup' && path !== 'mail') return;
    try { localStorage.setItem(MEMORY, path); } catch (e) {}
  }

  /* Scheduling attributes belong to ONE path. /cart/clear.js empties line items
     but leaves attributes, so a Will Call or pickup schedule chosen on one path
     survived a switch and rode into the next order verbatim — Eileen's mail test
     order #1024 (8/30) carried pickup_location "Will Call" and a bake-sheet
     delivery_date into the fields Chrysalis reads. Every path write therefore
     blanks the schedule; the scheduler re-stamps the current path's values on
     the next cart render. */
  var SCHEDULE_ATTRS = ['pickup_date', 'pickup_time', 'pickup_location',
                        'delivery_date', 'pick-up-location', 'ship_date',
                        // A8(d): the Mail Order estimate the confirmation email
                        // quotes back. Same reason as the rest of this list — a
                        // ship date from the path the shopper just left is worse
                        // than none. Keep in sync with STALE_SCHEDULE in
                        // mariposa-scheduler.js.
                        'ship_date_human', 'arrival_estimate',
                        'ws_fulfillment'];

  /* STATUS READ AND EFFECT VERIFIED (2026-09-06, MNR-7). This used to return the
     bare fetch, and applyPath reloaded on both then and catch — so a refused write
     reloaded the page on the OLD path with the OLD schedule attributes still set,
     which is precisely the stranding the SCHEDULE_ATTRS blanking above exists to
     prevent. A 4xx/5xx does not reject a fetch promise, and this store answers 429
     on cart mutations often (measured: /cart/clear.js needed 23 retries in one
     run), so the refusal path is ordinary rather than exotic. Same defect that
     produced A9 on the line stamps. */
  function writePath(path, attempt) {
    attempt = attempt || 0;
    var want = (function () {
      var a = {};
      a[ATTR] = path;
      SCHEDULE_ATTRS.forEach(function (k) { a[k] = ''; });
      return a;
    })();
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: want, note: '' })
    })
      .then(function (r) {
        if (r.ok) return null;
        /* 429 (cart throttling) carries Retry-After in seconds; honour it, capped. */
        var ra = parseFloat(r.headers.get('Retry-After'));
        if (window.console) console.warn('[mariposa-path] /cart/update.js', r.status);
        return { wait: ra > 0 ? Math.min(ra * 1000, 4000) : 0 };
      })
      .catch(function () { return { wait: 0 }; })
      .then(function (refused) {
        // Verify against the server. The response can be fine and the state wrong.
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var a = (cart && cart.attributes) || {};
            var wrong = !!cart.note || a[ATTR] !== path || SCHEDULE_ATTRS.some(function (k) { return a[k]; });
            if (!wrong) return true;
            /* Tom, 2026-10-09: "We could not switch shopping options" fired on a
               Pickup -> Mail Order switch that succeeded on retry. Four tries inside
               ~3s gave up too early under cart throttling; six tries over ~12s. */
            if (attempt >= 5) return false;
            return new Promise(function (res) { setTimeout(res, refused ? Math.max(refused.wait, 750 * (attempt + 1)) : 0); })
              .then(function () { return writePath(path, attempt + 1); });
          })
          .catch(function () { return false; });
      });
  }

  /* MNR-7 — read the status AND verify the effect. A 4xx/5xx does not reject a
     fetch promise, and this store answers 429 on cart mutations routinely, so a
     bare `.catch(function(){})` discards refusals in silence. Same helper shape
     as mariposa-scheduler.js. */
  function writeCartAttributes(want, attempt) {
    attempt = attempt || 0;
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: want })
    })
      .then(function (r) { return r.ok ? null : 'refused'; })
      .catch(function () { return 'refused'; })
      .then(function (refused) {
        return fetch('/cart.js', { headers: { Accept: 'application/json' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            var a = (cart && cart.attributes) || {};
            var wrong = Object.keys(want).some(function (k) { return (a[k] || '') !== (want[k] || ''); });
            if (!wrong) return true;
            if (attempt >= 3) return false;
            return new Promise(function (res) { setTimeout(res, refused ? 500 * (attempt + 1) : 0); })
              .then(function () { return writeCartAttributes(want, attempt + 1); });
          });
      })
      .catch(function () { return false; });
  }

  var LABEL = { pickup: 'Pickup', mail: 'Mail Order', wholesale: 'Wholesale' };

  function cartState() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('Could not read cart'); return r.json(); })
      .then(function (cart) {
        if (!cart || !Array.isArray(cart.items) || typeof cart.item_count !== 'number') throw new Error('Invalid cart response');
        return cart;
      });
  }

  /* STATUS READ AND EFFECT VERIFIED (2026-09-06, MNR-7). The shopper has just
     approved emptying the cart. A refused /cart/clear.js used to be swallowed, and
     applyPath then switched the path with the incompatible lines still in the
     cart — audit 2026-08-21 finding 12, the lines stranded to fail at checkout
     with no explanation, which is the whole reason this confirmation exists. */
  function clearCart(attempt) {
    attempt = attempt || 0;
    return fetch('/cart/clear.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}'
    }).catch(function () { return null; })
      .then(cartState)
      .then(function (cart) {
        if (cart.item_count === 0) return true;
        // Some stale carts reject clear.js (Eileen report 2665ada9). Remove
        // the current line keys together, then read back instead of assuming
        // either endpoint succeeded. Never create a new cart or bypass consent.
        var updates = {};
        cart.items.forEach(function (item) { updates[item.key] = 0; });
        return fetch('/cart/update.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).catch(function () { return null; })
          .then(cartState)
          .then(function (remaining) {
            if (remaining.item_count === 0) return true;
            if (attempt >= 2) return false;
            return new Promise(function (res) { setTimeout(res, 500 * (attempt + 1)); })
              .then(function () { return clearCart(attempt + 1); });
          });
      }).catch(function () { return false; });
  }

  /* Pickup and Mail Order sell different variants of the same product, so a
     cart built on one path cannot be bought on the other. Switching used to
     write the new path and reload, stranding the old lines to fail at checkout
     with no explanation (audit 2026-08-21, finding 12). Emptying is the honest
     outcome, but the shopper approves it. */
  function confirmSwitch(path, current, count) {
    var box = document.querySelector('[data-mb-switch-confirm]');
    /* No dialog in the theme means no way to ask. Switch, but never empty a
       cart the shopper was not asked about. */
    if (!box) return Promise.resolve('switch-only');
    var set = function (sel, text) {
      var el = box.querySelector(sel);
      if (el) el.textContent = text;
    };
    set('[data-mb-switch-target]', LABEL[path] || 'the other way to order');
    set('[data-mb-switch-current]', LABEL[current] || 'your current way to order');
    set('[data-mb-switch-stay]', LABEL[current] || 'your current way to order');
    set('[data-mb-switch-count]', count === 1 ? '1 item' : count + ' items');

    box.hidden = false;
    document.documentElement.classList.add('mb-gate-open');
    var go = box.querySelector('[data-mb-switch-go]');
    if (go) go.focus();

    return new Promise(function (resolve) {
      function close(answer) {
        box.hidden = true;
        document.documentElement.classList.remove('mb-gate-open');
        box.removeEventListener('click', onClick);
        document.removeEventListener('keydown', onKey);
        resolve(answer);
      }
      function onClick(e) {
        if (e.target.closest('[data-mb-switch-go]')) { e.preventDefault(); close(true); }
        else if (e.target.closest('[data-mb-switch-cancel]')) { e.preventDefault(); close(false); }
      }
      function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); close(false); } }
      box.addEventListener('click', onClick);
      document.addEventListener('keydown', onKey);
    });
  }

  function applyPath(path, goHome) {
    if (path === 'wholesale') {
      /* Tom, 2026-09-23: a viewer without wholesale access lands on the
         prospect page (info + inquiry + sign-in), not straight on login.
         Only non-B2B viewers ever reach this branch — a signed-in B2B
         contact resolves to the wholesale path automatically and the gate
         and mode bar don't offer this choice to them. */
      window.location.href = '/pages/wholesale';
      return Promise.resolve();
    }
    return writePath(path).then(function (saved) {
      if (!saved) { window.alert('We could not switch shopping options. Please try again.'); return; }
      remember(path);
      if (goHome) window.location.href = '/';
      else window.location.reload();
    });
  }

  var choosing = false;
  function choose(path, goHome) {
    if (choosing) return;
    choosing = true;
    var current = document.documentElement.getAttribute('data-mariposa-path') || '';
    if (!LABEL[current]) current = '';
    var status;
    function finish() {
      choosing = false;
      if (status) status.remove();
    }
    function showBusy() {
      status = document.createElement('div');
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.setAttribute('data-mb-switch-progress', '');
      status.textContent = 'Switching stores… Please wait.';
      status.style.cssText = 'position:fixed;bottom:24px;left:50%;transform:translateX(-50%);z-index:10000;background:#edf5fa;border:1px solid #bed6e4;border-radius:12px;padding:16px 24px;color:#233f50;box-shadow:0 2px 12px #0002';
      document.body.appendChild(status);
    }
    // After logout the resolved path can be empty while wholesale lines remain.
    // Read the cart even at the entry gate; lack of a path is not an empty cart.
    cartState().then(function (cart) {
      var previous = current || (cart.attributes && cart.attributes.shopping_mode) || '';
      var needsClear = cart.item_count > 0 && previous !== path;
      return (needsClear ? confirmSwitch(path, previous, cart.item_count) : Promise.resolve('keep'))
        .then(function (ok) {
          if (!ok) { finish(); return; }
          if (ok === 'switch-only') {
            finish();
            window.alert('Please open your cart and remove its items before switching stores.');
            return;
          }
          showBusy();
          return (needsClear ? clearCart() : Promise.resolve(true)).then(function (cleared) {
            if (!cleared) {
              finish();
              window.alert('Your cart could not be cleared. Your shopping option has not changed. Please try again.');
              return;
            }
            return applyPath(path, goHome).then(finish);
          });
        });
    }).catch(function () {
      finish();
      window.alert('We could not check your cart. Your shopping option has not changed. Please try again.');
    });
  }


  /* Theme editor (2026-09-29, Patti 9/28): the editor preview never has a
     path chosen, so the full-screen gate covered every page being edited.
     In the editor the gate stays closed and a small corner button shows or
     hides it, so its wording can still be checked. The storefront is
     unchanged. */
  var inEditor = !!(window.Shopify && window.Shopify.designMode);
  function editorPreviewButton() {
    if (document.querySelector('[data-mb-gate-preview]')) return;
    var b = document.createElement('button');
    b.type = 'button';
    b.setAttribute('data-mb-gate-preview', '');
    b.textContent = 'Preview store-selection popup';
    b.style.cssText = 'position:fixed;right:16px;bottom:16px;z-index:10000;padding:8px 12px;border:1px solid #7a3b24;border-radius:6px;background:#fff;color:#7a3b24;font:600 13px/1.2 sans-serif;cursor:pointer;box-shadow:0 2px 6px rgba(0,0,0,.15)';
    b.addEventListener('click', function () {
      var gate = document.querySelector('[data-mb-gate]');
      if (!gate) return;
      if (gate.hidden) { openGate(true); b.textContent = 'Hide store-selection popup'; }
      else { gate.hidden = true; document.documentElement.classList.remove('mb-gate-open'); b.textContent = 'Preview store-selection popup'; }
    });
    (document.body || document.documentElement).appendChild(b);
  }

  function openGate(force) {
    var gate = document.querySelector('[data-mb-gate]');
    if (!gate) return;
    if (inEditor && force !== true) { editorPreviewButton(); return; }
    gate.hidden = false;
    document.documentElement.classList.add('mb-gate-open');
    // Focus the popup, not its first choice: focusing Pickup made it look pre-selected
    // (browser audit 2026-10-26). Tab still moves to the choices.
    var panel = gate.querySelector('.mb-gate__panel');
    if (panel) { panel.setAttribute('tabindex', '-1'); panel.style.outline = 'none'; panel.focus({ preventScroll: true }); }
  }

  document.addEventListener('click', function (e) {
    var pick = e.target.closest && e.target.closest('[data-mb-choose]');
    if (pick) { e.preventDefault(); choose(pick.getAttribute('data-mb-choose')); return; }

    var sw = e.target.closest && e.target.closest('[data-mb-switch]');
    if (sw) { e.preventDefault(); choose(sw.getAttribute('data-mb-switch'), true); return; }

    var reopen = e.target.closest && e.target.closest('[data-mb-reopen]');
    if (reopen) { e.preventDefault(); openGate(); return; }
  });

  /* Keep the gate from being escaped with the keyboard. There is no dismiss:
     the only exits are choosing a path or leaving the site. */
  document.addEventListener('keydown', function (e) {
    var gate = document.querySelector('[data-mb-gate]');
    if (!gate || gate.hidden) return;
    if (e.key === 'Escape') e.preventDefault();
    if (e.key === 'Tab') {
      var f = gate.querySelectorAll('button, [href]');
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  /* Wholesale is granted by login, so choosing it redirects to /account/login
     and never runs writePath — the cart's shopping_mode keeps whatever the
     shopper last set (usually 'mail' from the gate default), and that stale
     value rides into the order's customAttributes. The order's CHANNEL is
     classified correctly downstream from company identity + ws_fulfillment
     (webhooks.orders.create.jsx, the checkout Function), so this never
     mistagged anything — but the raw attribute self-contradicted a Will Call
     wholesale order (QA 2026-09-04). Stamp it to match the resolved path so the
     data the fulfilment team and Chrysalis read is consistent. Writing
     'wholesale' is inert server-side: mariposa-path-value only honours it via
     b2b identity, never from the attribute, so it cannot forge a path. */
  function stampWholesale() {
    if ((document.documentElement.getAttribute('data-mariposa-path') || '') !== 'wholesale') return;
    cartState().then(function (cart) {
      if (cart && cart.attributes && cart.attributes.shopping_mode === 'wholesale') return;
      /* Status read and effect verified (MNR-7). Fired and forgotten, a refusal
         left shopping_mode unset on the very order this exists to label, and the
         guard only re-runs on the next page load. */
      return writeCartAttributes({ shopping_mode: 'wholesale' });
    }).catch(function () { /* Leave the cart unchanged; retry on the next page. */ });
  }

  /* Shopify admin "View" on a product (Patti, 2026-10-20: "When I edit the
     Animal and then click Preview, it takes me to the Mail Order site").
     Admin opens the plain product URL in a new tab, so the page took the path
     of whatever her browser last used. A product sold on ONE retail path now
     opens on that path, but ONLY when the page was opened from Shopify admin
     (document.referrer is https://admin.shopify.com/, verified 2026-10-20 by
     clicking View on The Animal). Shoppers never arrive from admin, so the
     storefront is unchanged for them: commit 6149168 applied the same switch
     to every outside visitor on 93 products and was reverted (0300d01).
     layout/theme.liquid writes data-mb-product-path on such a product page.
     Refused when ?mode= is in the URL, in the theme editor, or when the cart
     has items (its lines belong to one path; never strand them). The reload
     is a navigation from this page, so its referrer is this page and the next
     load cannot switch again; a refused or failed write falls back to
     settle(), the behaviour before this existed. */
  function adminPreviewPath(current, explicit) {
    var sole = document.documentElement.getAttribute('data-mb-product-path') || '';
    if (inEditor || explicit || (sole !== 'pickup' && sole !== 'mail') || sole === current) return '';
    var from = '';
    try { from = document.referrer ? new URL(document.referrer).hostname : ''; } catch (e) {}
    // A visitor with no path yet (e.g. from Google, an email or an old link) opens on the product's
    // only path instead of the popup (Tom 2026-10-26). Products on both paths still ask.
    return (from === 'admin.shopify.com' || current === 'none') ? sole : '';
  }

  function openOnPath(sole, current) {
    cartState().then(function (cart) {
      // A manual switch started while the cart was read: it writes and
      // navigates on its own, and settle() would write the old path back.
      if (choosing) return 'manual';
      if (cart.item_count !== 0) return false;
      choosing = true;
      return writePath(sole).then(function (saved) {
        choosing = false;
        if (!saved) return false;
        remember(sole);
        window.location.replace(window.location.pathname + window.location.search);
        return true;
      });
    }).then(function (done) {
      if (!done) settle(current);
    }, function () {
      choosing = false;
      settle(current);
    });
  }

  function init() {
    /* Deep links such as /collections/all?mode=mail (homepage hero, legacy
       links) set the path and reload. 'all' is no longer accepted: there is no
       unlocked view to return to. */
    var m = /[?&]mode=(pickup|mail)/.exec(window.location.search);
    var current = document.documentElement.getAttribute('data-mariposa-path') || 'none';
    if (current === 'wholesale') { stampWholesale(); return; }
    if (m && m[1] !== current) { choose(m[1]); return; }
    var sole = adminPreviewPath(current, !!m);
    if (sole) { openOnPath(sole, current); return; }
    settle(current);
  }

  function settle(current) {
    // Liquid resolves a homogeneous Buy Again cart from the actual variants.
    // Sync its attribute without removing items or the customer's note.
    if (current === 'pickup' || current === 'mail') {
      remember(current);
      cartState().then(function (cart) {
        if ((cart.attributes || {})[ATTR] === current) return;
        var want = {}; want[ATTR] = current;
        SCHEDULE_ATTRS.forEach(function (k) { want[k] = ''; });
        return writeCartAttributes(want);
      }).catch(function () {});
      return;
    }
    // Checkout creates a new cart. Restore only an EMPTY retail cart, never
    // wholesale access or a cart whose products might belong to another mode.
    var remembered = '';
    try { remembered = localStorage.getItem(MEMORY); } catch (e) {}
    if (remembered !== 'pickup' && remembered !== 'mail') { openGate(); return; }
    cartState().then(function (cart) {
      if (cart.item_count !== 0 || choosing) { openGate(); return; }
      var want = {}; want[ATTR] = remembered;
      return writeCartAttributes(want).then(function (saved) {
        if (saved) window.location.reload(); else openGate();
      });
    }).catch(openGate);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
